/* eslint-disable i18next/no-literal-string -- const enum values mirror the backend HoldingStatusKind / OriginKind enums */

import {BadgeVariant} from '@/components/ui/badge'
import {EnumValue} from '@/components/shared/EnumValue'
import type {useI18n} from '@/i18n'

/**
 * Provenance-aware classification of "does this identity hold this target".
 * Mirrors the backend {@code com.irian.iam.access.api.HoldingStatusKind} enum
 * and is kept in sync by the `RequireEnumTypescriptMatch` convention check.
 *
 * <ul>
 *   <li>{@code NONE} — no active grant; re-request allowed</li>
 *   <li>{@code DIRECT_BLOCKING} — durable direct grant (REQUEST or API source);
 *       re-request is structurally redundant and the gate rejects it</li>
 *   <li>{@code INDIRECT_RULE} / {@code INDIRECT_IMPORT} / {@code INDIRECT_FG} —
 *       active but ephemeral; re-request is a durability upgrade</li>
 *   <li>{@code INDIRECT_MIXED} — multiple non-blocking origins of different kinds</li>
 * </ul>
 *
 * {@code INDIRECT_FG} is only ever emitted by the AP-side endpoint; FG-side
 * endpoints (function groups don't compose) never produce it.
 */
export const HoldingStatusKind = {
    NONE: 'NONE',
    DIRECT_BLOCKING: 'DIRECT_BLOCKING',
    INDIRECT_RULE: 'INDIRECT_RULE',
    INDIRECT_IMPORT: 'INDIRECT_IMPORT',
    INDIRECT_FG: 'INDIRECT_FG',
    INDIRECT_MIXED: 'INDIRECT_MIXED',
} as const

export type HoldingStatusKind = typeof HoldingStatusKind[keyof typeof HoldingStatusKind]

/**
 * One concrete origination path by which an identity holds a target. Mirrors
 * the backend {@code com.irian.iam.access.api.HoldingOrigin.OriginKind} enum.
 *
 * {@code FG} only appears on the AP-side; FG-side endpoints never emit it.
 */
export const HoldingOriginKind = {
    REQUEST: 'REQUEST',
    API: 'API',
    RULE: 'RULE',
    IMPORT: 'IMPORT',
    FG: 'FG',
} as const

export type HoldingOriginKind = typeof HoldingOriginKind[keyof typeof HoldingOriginKind]

/**
 * One row of provenance backing a {@link HoldingStatusResponse}. The
 * {@link label} is human-readable (rule name, system name, FG name, "Access
 * Request 1234"); {@link validTo} is {@code null} for permanent grants.
 *
 * Mirrors the backend {@code HoldingStatusResponse.HoldingOriginResponse} record.
 */
export interface HoldingOriginResponse {
    readonly kind: HoldingOriginKind
    readonly label: string
    readonly validFrom: string
    readonly validTo: string | null
}

/**
 * Response shape from {@code GET /api/i/access-requests/profile-holdings} and
 * {@code GET /api/i/function-group-requests/holdings} — one entry per requested
 * id, keyed by the target id in the returned map.
 *
 * Mirrors the backend {@code com.irian.iam.access.api.HoldingStatusResponse} record.
 */
export interface HoldingStatusResponse {
    readonly kind: HoldingStatusKind
    readonly origins: ReadonlyArray<HoldingOriginResponse>
}

type I18n = ReturnType<typeof useI18n>

/**
 * Rich enum carrying UI rendering metadata (badge variant + i18n label) for
 * {@link HoldingStatusKind} wire values. The picker decoration reads the
 * variant + label off the enum constant directly; the gate logic reads
 * {@link isBlocking}.
 */
export class HoldingStatusKindBadge extends EnumValue {
    constructor(
        code: string,
        variant: BadgeVariant,
        labelFn: (i18n: I18n) => string,
        public readonly blocking: boolean,
    ) {
        super(code, variant, labelFn)
    }

    static readonly NONE = new HoldingStatusKindBadge(
        HoldingStatusKind.NONE, BadgeVariant.OUTLINE,
        (i18n) => i18n.holdingStatus.none, false,
    )
    static readonly DIRECT_BLOCKING = new HoldingStatusKindBadge(
        HoldingStatusKind.DIRECT_BLOCKING, BadgeVariant.DESTRUCTIVE,
        (i18n) => i18n.holdingStatus.directBlocking, true,
    )
    static readonly INDIRECT_RULE = new HoldingStatusKindBadge(
        HoldingStatusKind.INDIRECT_RULE, BadgeVariant.SECONDARY,
        (i18n) => i18n.holdingStatus.indirectRule, false,
    )
    static readonly INDIRECT_IMPORT = new HoldingStatusKindBadge(
        HoldingStatusKind.INDIRECT_IMPORT, BadgeVariant.SECONDARY,
        (i18n) => i18n.holdingStatus.indirectImport, false,
    )
    static readonly INDIRECT_FG = new HoldingStatusKindBadge(
        HoldingStatusKind.INDIRECT_FG, BadgeVariant.SECONDARY,
        (i18n) => i18n.holdingStatus.indirectFg, false,
    )
    static readonly INDIRECT_MIXED = new HoldingStatusKindBadge(
        HoldingStatusKind.INDIRECT_MIXED, BadgeVariant.SECONDARY,
        (i18n) => i18n.holdingStatus.indirectMixed, false,
    )

    static readonly values: ReadonlyArray<HoldingStatusKindBadge> = [
        HoldingStatusKindBadge.NONE,
        HoldingStatusKindBadge.DIRECT_BLOCKING,
        HoldingStatusKindBadge.INDIRECT_RULE,
        HoldingStatusKindBadge.INDIRECT_IMPORT,
        HoldingStatusKindBadge.INDIRECT_FG,
        HoldingStatusKindBadge.INDIRECT_MIXED,
    ]

    static of(code: HoldingStatusKind | null | undefined): HoldingStatusKindBadge | undefined {
        return HoldingStatusKindBadge.values.find((v) => v.code === code)
    }
}
